import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../api/axiosClient";

import DashboardSidebar from "../../components/craftsman/DashboardSidebar";
import DashboardTab     from "../../components/craftsman/DashboardTab";
import AnalyticsTab     from "../../components/craftsman/AnalyticsTab";
import ProfileTab       from "../../components/craftsman/ProfileTab";
import JobsTab          from "../../components/craftsman/JobsTab";
import MembersTab       from "../../components/craftsman/MembersTab";
import SettingsTab      from "../../components/craftsman/SettingsTab";
import ProductsTab      from "../../components/craftsman/ProductsTab";
import FinanceTab       from "../../components/craftsman/FinanceTab";
import MessageTab       from "../../components/craftsman/MessageTab";
import ScheduleTab      from "../../components/craftsman/ScheduleTab";
import ReviewsTab       from "../../components/craftsman/ReviewsTab";
import SupportTab       from "../../components/craftsman/SupportTicketsTab";

import { getFullImageUrl } from "../../utils/getFullImageUrl";

const UNREAD_POLL_MS = 20000;

// ─── Small helpers ───────────────────────────────────────────────────────────

// Accepts a plain array or a DRF paginated response ({ results: [...] }).
const toArray = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];

// ProfileTab stores specialisations as an array, but the API may send a
// comma-separated string.
const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    return value.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const normalizeServices = (services) =>
  Array.isArray(services)
    ? services.map((s) => ({
        id:   s.id,
        name: s.name,
        rate: s.rate ? parseFloat(s.rate) : null,
        unit: s.unit || "fixed",
      }))
    : [];

const normalizeGallery = (gallery) =>
  Array.isArray(gallery)
    ? gallery.map((img) => ({
        id: img.id,
        url: img.image_url,
        preview: getFullImageUrl(img.image_url),
        isExisting: true,
      }))
    : [];

// Turns DRF error payloads into one readable line.
const extractError = (err, fallback) => {
  const d = err?.response?.data;
  if (!d) {
    return err?.message === "Network Error"
      ? "Network error. Please check your connection."
      : fallback;
  }
  if (typeof d === "string") return d.length < 200 ? d : fallback;
  if (d.detail) return String(d.detail);
  if (typeof d === "object") {
    const [field, val] = Object.entries(d)[0] || [];
    if (field) {
      const first = Array.isArray(val) ? val[0] : val;
      return `${field}: ${typeof first === "string" ? first : JSON.stringify(first)}`;
    }
  }
  return fallback;
};

// ─── Toast system ────────────────────────────────────────────────────────────
const TOAST_ICONS  = { success: "✅", error: "❌", warning: "⚠️", info: "ℹ️" };
const TOAST_STYLES = {
  success: { bg: "#f0fdf4", border: "#86efac", color: "#15803d", bar: "#22c55e" },
  error:   { bg: "#fef2f2", border: "#fca5a5", color: "#991b1b", bar: "#ef4444" },
  warning: { bg: "#fffbeb", border: "#fde68a", color: "#92400e", bar: "#f59e0b" },
  info:    { bg: "#f0fdf4", border: "#86efac", color: "#15803d", bar: "#22c55e" },
};

function ToastContainer({ toasts, removeToast }) {
  return (
    <>
      <style>{`
        .toast-container { position:fixed; top:1.5rem; right:1.5rem; z-index:9999; display:flex; flex-direction:column; gap:.75rem; max-width:380px; width:calc(100vw - 3rem); pointer-events:none; }
        .toast-item { pointer-events:all; border-radius:14px; padding:1rem 1.25rem; display:flex; align-items:flex-start; gap:10px; box-shadow:0 8px 32px rgba(0,0,0,.12); border:2px solid; position:relative; overflow:hidden; animation:toastIn .35s cubic-bezier(.34,1.56,.64,1) forwards; font-family:'Raleway',sans-serif; }
        .toast-item.removing { animation:toastOut .25s ease-in forwards; }
        @keyframes toastIn  { from{opacity:0;transform:translateX(60px) scale(.92)} to{opacity:1;transform:translateX(0) scale(1)} }
        @keyframes toastOut { from{opacity:1;transform:translateX(0) scale(1);max-height:200px} to{opacity:0;transform:translateX(60px) scale(.9);max-height:0;padding:0;margin:0} }
        .toast-icon  { font-size:1.25rem; flex-shrink:0; line-height:1.3; }
        .toast-body  { flex-grow:1; }
        .toast-title { font-weight:800; font-size:.9rem; margin-bottom:2px; }
        .toast-msg   { font-size:.85rem; font-weight:500; opacity:.9; line-height:1.4; }
        .toast-close { background:none; border:none; cursor:pointer; font-size:1.1rem; line-height:1; padding:0; flex-shrink:0; opacity:.6; transition:opacity .2s; margin-top:-2px; }
        .toast-close:hover { opacity:1; }
        .toast-bar   { position:absolute; bottom:0; left:0; height:3px; border-radius:0 0 14px 14px; animation:toastBar var(--duration,4s) linear forwards; }
        @keyframes toastBar { from{width:100%} to{width:0%} }
        @media (max-width:480px) { .toast-container { top:1rem; right:1rem; left:1rem; width:auto; } }
      `}</style>
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((t) => {
          const s = TOAST_STYLES[t.type] || TOAST_STYLES.info;
          return (
            <div
              key={t.id}
              className={`toast-item ${t.removing ? "removing" : ""}`}
              style={{ background: s.bg, borderColor: s.border, color: s.color, "--duration": `${t.duration / 1000}s` }}
            >
              <span className="toast-icon">{TOAST_ICONS[t.type] || TOAST_ICONS.info}</span>
              <div className="toast-body">
                {t.title && <div className="toast-title">{t.title}</div>}
                <div className="toast-msg">{t.message}</div>
              </div>
              <button
                className="toast-close"
                onClick={() => removeToast(t.id)}
                aria-label="Dismiss notification"
                style={{ color: s.color }}
              >
                ×
              </button>
              <div className="toast-bar" style={{ background: s.bar }} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Set());

  const schedule = useCallback((fn, ms) => {
    const handle = setTimeout(() => {
      timers.current.delete(handle);
      fn();
    }, ms);
    timers.current.add(handle);
  }, []);

  const removeToast = useCallback(
    (id) => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, removing: true } : t)));
      schedule(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 280);
    },
    [schedule]
  );

  const addToast = useCallback(
    (message, type = "info", title = "", duration = 4000) => {
      const id = Date.now() + Math.random();
      // Keep at most 5 toasts on screen.
      setToasts((prev) => [...prev.slice(-4), { id, message, type, title, duration, removing: false }]);
      schedule(() => removeToast(id), duration);
    },
    [removeToast, schedule]
  );

  // Clear pending timers on unmount.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  return { toasts, addToast, removeToast };
}

// ─── Profile completeness banner ─────────────────────────────────────────────
function ProfileCompletenessBanner({ completeness, missingItems, onJumpToProfile }) {
  if (completeness >= 100) return null;

  return (
    <>
      <style>{`
        .completeness-banner {
          border-radius: 16px;
          padding: 1.25rem 1.5rem;
          margin-bottom: 1.5rem;
          background: linear-gradient(135deg, rgba(255,240,0,.10), rgba(34,197,94,.10));
          border: 2px solid rgba(34,197,94,.25);
          font-family: 'Raleway', sans-serif;
        }
        .completeness-top {
          display: flex; align-items: center; justify-content: space-between;
          gap: 1rem; flex-wrap: wrap; margin-bottom: .75rem;
        }
        .completeness-title { font-weight: 800; font-size: 1rem; color: #1f2937; margin: 0; }
        .completeness-pct   { font-weight: 800; font-size: .95rem; color: #15803d; }
        .completeness-track {
          height: 10px; border-radius: 6px; background: rgba(0,0,0,.08);
          overflow: hidden; margin-bottom: 1rem;
        }
        .completeness-fill {
          height: 100%; border-radius: 6px;
          background: linear-gradient(90deg, #fff000, #22c55e);
          transition: width .4s ease;
        }
        .completeness-items { display: flex; flex-wrap: wrap; gap: .6rem; }
        .completeness-chip {
          cursor: pointer; border-radius: 999px;
          padding: .45rem 1rem; font-size: .82rem; font-weight: 700;
          background: white; color: #15803d; border: 1.5px solid #86efac;
          transition: transform .15s, background .15s;
        }
        .completeness-chip:hover { background: #f0fdf4; transform: translateY(-1px); }
        @media (max-width: 480px) {
          .completeness-banner { padding: 1rem; }
        }
      `}</style>

      <div className="completeness-banner">
        <div className="completeness-top">
          <p className="completeness-title">Complete your profile to get more clients</p>
          <span className="completeness-pct">{completeness}% complete</span>
        </div>

        <div
          className="completeness-track"
          role="progressbar"
          aria-valuenow={completeness}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="completeness-fill" style={{ width: `${completeness}%` }} />
        </div>

        <div className="completeness-items">
          {missingItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className="completeness-chip"
              onClick={() => onJumpToProfile(item.key)}
            >
              + Add {item.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function DashboardPage() {
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToast();

  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);

  const [craftsman,   setCraftsman]   = useState({});
  const [profileData, setProfileData] = useState({
    description: "",
    profession: "",
    experience_level: "",
    location: "",
    company_name: "",
    specialisation: [],   
    services: [],
    account_type: "Individual",
  });

  const [profileImage,         setProfileImage]         = useState(null);
  const [proofDocument,        setProofDocument]        = useState(null);
  const [portfolioImages,      setPortfolioImages]      = useState([]);
  const [portfolioIdsToRemove, setPortfolioIdsToRemove] = useState([]);

  const [jobs,      setJobs]      = useState([]);
  const [reviews,   setReviews]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [activeTab, setActiveTab] = useState("Profile");

  const [profileFocusSection, setProfileFocusSection] = useState(null);

  const [unreadMessages, setUnreadMessages] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const accountType = profileData.account_type || craftsman.account_type || "Individual";
  const activeRole  = craftsman.active_role || "craftsman";

  // ── Data loading ───────────────────────────────────────────────────────────
  const fetchUserRole = useCallback(async () => {
    try {
      const { data } = await api.get("/me/");
      if (!mountedRef.current) return;
      setCraftsman((prev) => ({ ...prev, active_role: data.active_role || "craftsman" }));
    } catch {
      // Non-critical: falls back to "craftsman".
    }
  }, []);

  const fetchCraftsmanData = useCallback(async () => {
    try {
      const { data } = await api.get("/craftsman/");
      if (!mountedRef.current) return;

      setCraftsman((prev) => ({ ...prev, ...data }));
      setProfileData({
        description:      data.description      || "",
        profession:       data.profession       || "",
        experience_level: data.experience_level || "",
        location:         data.location         || "",
        company_name:     data.company_name     || "",
        account_type:     data.account_type     || "Individual",
        // Backend field is "specialisation"; fall back to legacy "skills".
        specialisation:   toList(data.specialisation ?? data.skills),
        services:         normalizeServices(data.services),
      });
      setProfileImage(data.profile_url        ? getFullImageUrl(data.profile_url)        : null);
      setProofDocument(data.proof_document_url ? getFullImageUrl(data.proof_document_url) : null);
      if (Array.isArray(data.gallery_images)) {
        setPortfolioImages(normalizeGallery(data.gallery_images));
      }
    } catch {
      if (mountedRef.current) {
        addToast("Failed to load your profile. Please refresh.", "error", "Load Error");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [addToast]);

  const fetchAssignedJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/job-requests/");
      if (mountedRef.current) setJobs(toArray(data));
    } catch {
      if (mountedRef.current) addToast("Could not load your jobs.", "error", "Jobs Error");
    }
  }, [addToast]);

  useEffect(() => {
    fetchCraftsmanData();
    fetchAssignedJobs();
    fetchUserRole();
  }, [fetchCraftsmanData, fetchAssignedJobs, fetchUserRole]);

  // Reviews load when the tab is opened. If your API uses a different route,
  // change the URL below; a missing endpoint just shows the empty state.
  useEffect(() => {
    if (activeTab !== "Reviews") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/craftsman/reviews/");
        if (!cancelled) setReviews(toArray(data));
      } catch {
        if (!cancelled) setReviews([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  // ── Unread badge (same sources MessageTab uses) ────────────────────────────
  const fetchUnreadCount = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;

    const [convoRes, alertRes] = await Promise.allSettled([
      api.get("/job-requests/"),
      api.get("/craftsman/notifications/"),
    ]);
    if (!mountedRef.current) return;

    // If both calls failed, keep the previous count instead of resetting to 0.
    if (convoRes.status === "rejected" && alertRes.status === "rejected") return;

    let count = 0;
    if (convoRes.status === "fulfilled") {
      count += toArray(convoRes.value.data).reduce((sum, c) => sum + (Number(c.unread) || 0), 0);
    }
    if (alertRes.status === "fulfilled") {
      count += toArray(alertRes.value.data).filter((a) => !a.read).length;
    }
    setUnreadMessages(count);
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, UNREAD_POLL_MS);
    const onVisible = () => { if (!document.hidden) fetchUnreadCount(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchUnreadCount]);

  // Re-sync the badge shortly after the person opens Messages.
  useEffect(() => {
    if (activeTab !== "Messages") return;
    const t = setTimeout(fetchUnreadCount, 1500);
    return () => clearTimeout(t);
  }, [activeTab, fetchUnreadCount]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleSwitchRole = async () => {
    if (switchingRole) return;
    const newRole = activeRole === "craftsman" ? "client" : "craftsman";
    setSwitchingRole(true);
    try {
      await api.post("/switch-role/", { role: newRole });

      let stored = {};
      try { stored = JSON.parse(localStorage.getItem("user") || "{}"); } catch { stored = {}; }
      stored.role = newRole;
      localStorage.setItem("user", JSON.stringify(stored));

      setCraftsman((prev) => ({ ...prev, active_role: newRole }));
      addToast(`Switched to ${newRole} mode!`, "success", "Role Switched 🔄", 2000);

      // Full reload so every part of the app picks up the new role.
      // switchingRole stays true until then so the button can't be clicked twice.
      setTimeout(() => {
        window.location.href = newRole === "client" ? "/hire" : "/craftsman-dashboard";
      }, 1000);
    } catch (err) {
      addToast(extractError(err, "Could not switch role. Please try again."), "error", "Switch Failed");
      setSwitchingRole(false);
    }
  };

  // ── Profile image / documents / portfolio ──────────────────────────────────
  const handleProfileImageChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setProfileImage(f);
  };

  const handleProofDocumentChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setProofDocument(f);
  };

  const handlePortfolioAdd = useCallback((imgObj) => {
    setPortfolioImages((prev) => [...prev, imgObj]);
  }, []);

  const handlePortfolioRemove = useCallback((id) => {
    setPortfolioImages((prev) => {
      const img = prev.find((i) => (i.id ?? i) === id);
      if (img?.isExisting && typeof img.id === "number") {
        setPortfolioIdsToRemove((ids) => (ids.includes(img.id) ? ids : [...ids, img.id]));
      }
      if (typeof img?.preview === "string" && img.preview.startsWith("blob:")) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter((i) => (i.id ?? i) !== id);
    });
  }, []);

  // Stable callback so ProfileTab's focus effect doesn't re-run every render.
  const handleFocusHandled = useCallback(() => setProfileFocusSection(null), []);

  // ── Save ───────────────────────────────────────────────────────────────────
  const saveProfile = async (formData) => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/craftsman/", formData);
      if (!mountedRef.current) return;

      setCraftsman((prev) => ({ ...prev, ...data }));
      setProfileImage(data.profile_url        ? getFullImageUrl(data.profile_url)        : null);
      setProofDocument(data.proof_document_url ? getFullImageUrl(data.proof_document_url) : null);
      if (Array.isArray(data.gallery_images)) {
        setPortfolioImages(normalizeGallery(data.gallery_images));
      }

      setProfileData((prev) => ({
        ...prev,
        description:      data.description      ?? prev.description,
        profession:       data.profession       ?? prev.profession,
        location:         data.location         ?? prev.location,
        company_name:     data.company_name     ?? prev.company_name,
        experience_level: data.experience_level || prev.experience_level,
        specialisation:
          data.specialisation !== undefined || data.skills !== undefined
            ? toList(data.specialisation ?? data.skills)
            : prev.specialisation,
        services: Array.isArray(data.services) ? normalizeServices(data.services) : prev.services,
      }));

      setPortfolioIdsToRemove([]);
      addToast("Your profile has been updated.", "success", "Profile Saved!", 5000);
    } catch (err) {
      addToast(extractError(err, "Something went wrong. Please try again."), "error", "Save Failed");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  // ── Profile completeness ───────────────────────────────────────────────────
  // Keys and rules mirror ProfileTab's own checklist and validation so the
  // banner's "Add …" buttons scroll to the right section.
  const { completeness, missingItems } = useMemo(() => {
    const checks = [
      { key: "photo",          label: "profile photo",    done: !!profileImage },
      { key: "description",    label: "description",      done: !!profileData.description?.trim() },
      { key: "profession",     label: "profession",       done: !!profileData.profession?.trim() },
      { key: "specialisation", label: "specialisation",   done: toList(profileData.specialisation).length > 0 },
      { key: "services",       label: "services",         done: (profileData.services || []).length > 0 },
      { key: "location",       label: "service area",     done: !!profileData.location?.trim() },
      { key: "portfolio",      label: "work photos",      done: portfolioImages.length > 0 },
    ];
    const doneCount = checks.filter((c) => c.done).length;
    return {
      completeness: Math.round((doneCount / checks.length) * 100),
      missingItems: checks.filter((c) => !c.done),
    };
  }, [profileImage, profileData, portfolioImages]);

  const jumpToProfileSection = (sectionKey) => {
    setProfileFocusSection(sectionKey);
    handleTabChange("Profile");
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#fff000,#22c55e)" }}>
        <div style={{ textAlign: "center" }}>
          <div className="spinner-border text-light" role="status" style={{ width: "4rem", height: "4rem" }} />
          <p className="mt-3 text-light fw-bold">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&display=swap');
        .dashboard-container {
          min-height: 100vh;
          background: white;
          display: flex;
          font-family: 'Raleway', sans-serif;
        }
        .main-content {
          flex-grow: 1;
          padding: 2.5rem;
          background: #f8fafc;
          min-height: 100vh;
          width: 100%;
          min-width: 0;
        }
        .content-card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0,0,0,.06);
          padding: 2.5rem;
          margin-bottom: 2rem;
          border: 2px solid rgba(251,191,36,.1);
          position: relative;
          overflow: hidden;
        }
        .content-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, #fff000, #22c55e);
        }
        .content-card.dark {
          background: transparent; box-shadow: none; padding: 0; border: none;
        }
        .content-card.dark::before { display: none; }
        .mobile-topbar {
          display: none;
          position: sticky; top: 0; z-index: 200;
          background: #1f2937; padding: .75rem 1rem;
          align-items: center; justify-content: space-between;
          box-shadow: 0 2px 12px rgba(0,0,0,.3);
        }
        .mobile-topbar-title {
          font-family: 'Raleway', sans-serif; font-weight: 800; font-size: 1.1rem;
          background: linear-gradient(135deg, #fff000, #22c55e);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .hamburger-btn {
          background: rgba(255,255,255,.1); border: none; border-radius: 10px;
          width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: background .2s;
        }
        .hamburger-btn:hover { background: rgba(255,255,255,.2); }
        .hamburger-icon { display: flex; flex-direction: column; gap: 5px; }
        .hamburger-icon span { display: block; width: 20px; height: 2px; background: white; border-radius: 2px; }
        .sidebar-overlay {
          display: none; position: fixed; inset: 0; background: rgba(0,0,0,.55);
          z-index: 299; animation: fadeIn .2s ease;
        }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @media (max-width: 991px) {
          .mobile-topbar { display: flex; }
          .sidebar-overlay { display: block; }
          .dashboard-container { flex-direction: column; }
          .main-content { padding: 1rem; }
          .content-card { padding: 1.25rem; border-radius: 14px; }
        }
        @media (max-width: 480px) {
          .main-content { padding: .75rem; }
          .content-card { padding: 1rem; border-radius: 12px; }
        }
      `}</style>

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="mobile-topbar">
        <span className="mobile-topbar-title">Kaakazini</span>
        <button
          className="hamburger-btn"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Toggle navigation menu"
          aria-expanded={sidebarOpen}
        >
          <div className="hamburger-icon"><span /><span /><span /></div>
        </button>
      </div>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="dashboard-container">
        <DashboardSidebar
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          handleLogout={handleLogout}
          accountType={accountType}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeRole={activeRole}
          onSwitchRole={handleSwitchRole}
          switchingRole={switchingRole}
          unreadMessages={unreadMessages}
        />

        <div className="main-content">

          {activeTab === "Dashboard" && (
            <div className="content-card">
              <ProfileCompletenessBanner
                completeness={completeness}
                missingItems={missingItems}
                onJumpToProfile={jumpToProfileSection}
              />
              <DashboardTab craftsman={craftsman} jobs={jobs} />
            </div>
          )}

          {activeTab === "Analytics" && (
            <div className="content-card dark">
              <AnalyticsTab craftsman={craftsman} jobs={jobs} />
            </div>
          )}

          {activeTab === "Profile" && (
            <div className="content-card">
              {/* ProfileTab loads its own trade taxonomy and falls back to
                  setProfileData for skills/services, so only the props it
                  actually uses are passed. */}
              <ProfileTab
                craftsman={craftsman}
                profileData={profileData}
                setProfileData={setProfileData}
                profileImage={profileImage}
                handleProfileImageChange={handleProfileImageChange}
                proofDocument={proofDocument}
                handleProofDocumentChange={handleProofDocumentChange}
                portfolioImages={portfolioImages}
                onPortfolioAdd={handlePortfolioAdd}
                onPortfolioRemove={handlePortfolioRemove}
                portfolioRemoveIds={portfolioIdsToRemove}
                saveProfile={saveProfile}
                saving={saving}
                addToast={addToast}
                focusSection={profileFocusSection}
                onFocusHandled={handleFocusHandled}
              />
            </div>
          )}

          {activeTab === "Jobs" && (
            <div className="content-card">
              <JobsTab jobs={jobs} setJobs={setJobs} userRole={activeRole} addToast={addToast} />
            </div>
          )}

          {activeTab === "Support" && (
            <div className="content-card">
              <SupportTab addToast={addToast} />
            </div>
          )}

          {activeTab === "Schedule" && (
            <div className="content-card">
              <ScheduleTab
                jobs={jobs}
                onViewJob={() => handleTabChange("Jobs")}
                onMessageClient={() => handleTabChange("Messages")}
                addToast={addToast}
              />
            </div>
          )}

          {activeTab === "Reviews" && (
            <div className="content-card">
              <ReviewsTab reviews={reviews} />
            </div>
          )}

          {activeTab === "Products" && (
            <div className="content-card">
              <ProductsTab addToast={addToast} />
            </div>
          )}

          {activeTab === "Finance" && (
            <div className="content-card">
              <FinanceTab craftsman={craftsman} jobs={jobs} addToast={addToast} />
            </div>
          )}

          {activeTab === "Messages" && (
            <div className="content-card">
              <MessageTab craftsman={craftsman} addToast={addToast} />
            </div>
          )}

          {activeTab === "Members" && accountType === "Company" && (
            <div className="content-card">
              <MembersTab craftsman={craftsman} addToast={addToast} />
            </div>
          )}

          {activeTab === "Settings" && (
            <div className="content-card">
              <SettingsTab craftsman={craftsman} addToast={addToast} />
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default DashboardPage;
